import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { Grid, IconButton, Button, TextField, Typography } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons/faPencilAlt";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";

import { handleDownloadDocsFiles } from "../../../services/documents/document-files-api.service";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

import { Props } from "./interfaces";
import { EditContainer, Header, EditActions } from "./styles";
import ShareDocument from "../ShareDocument/ShareDocument";
import { DocumentDTO } from "../../../models/documents";

const { UploadDocuments, DownloadDocuments } = AUTHORIZATIONS.LegalEntityAccess;
const { CompanyAccountsConfig } = AUTHORIZATIONS.SystemAccess;

const EditDocumentName = ({
  document,
  saveDocument,
  deleteDocument,
  documentIsReadOnly,
  customDocAndFilesNames,
  allowShare = true,
  allowDownloadContent = true,
  isGenericDocument,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.document.actions"; // tRP - translation root path
  const theme = useTheme();
  const [editable, setEditable] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(document?.name);

  const allowUpload = useAuthorization([UploadDocuments]);
  const allowDownload = useAuthorization([DownloadDocuments]);
  const isSysAdmin = useAuthorization([CompanyAccountsConfig]);

  return (
    <>
      {editable ? (
        <EditContainer>
          <TextField
            variant="standard"
            fullWidth
            value={inputValue}
            label={`${t(`${tRP}.edit_document_name`)}`}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                saveDocument(inputValue);
                setEditable(false);
              }
            }}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <EditActions>
            <Button
              // color="secondary"
              variant="outlined"
              onClick={() => {
                setInputValue(document?.name);
                setEditable(false);
              }}
              style={{
                color: theme.palette.actions.cancel,
                border: theme.palette.actions.cancel,
              }}
            >
              {`${t("buttons.cancel")}`}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                saveDocument(inputValue);
                setEditable(false);
              }}
            >
              {`${t("buttons.save")}`}
            </Button>
          </EditActions>
        </EditContainer>
      ) : (
        <Header>
          <Typography title={document?.name || "Document name is empty..."}>{document?.name}</Typography>
          <Grid>
            {(allowUpload || isSysAdmin) && !documentIsReadOnly && (
              <IconButton
                title={t(`${tRP}.edit_document_name`)}
                onClick={() => setEditable(true)}
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </IconButton>
            )}
            {allowShare && <ShareDocument document={document as DocumentDTO} />}
            {(allowDownload || isSysAdmin || allowDownloadContent) && (
              <IconButton
                title={t(`${tRP}.download_document_files`)}
                onClick={() =>
                  handleDownloadDocsFiles([document?.id], customDocAndFilesNames, undefined, isGenericDocument)
                }
              >
                <FontAwesomeIcon icon={faDownload} />
              </IconButton>
            )}
            {(allowUpload || isSysAdmin) && !documentIsReadOnly && (
              <IconButton
                title={t(`${tRP}.delete_document`)}
                onClick={deleteDocument}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            )}
          </Grid>
        </Header>
      )}
    </>
  );
};

export default EditDocumentName;
